import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, RefreshCcw, ShieldCheck } from "lucide-react";
import api, { formatApiErrorDetail } from "@/api/client";
import { useAuth } from "@/context/AuthContext";

const CHECKOUT_SCRIPT = "https://checkout.razorpay.com/v1/checkout.js";

async function loadRazorpayScript() {
  if (window.Razorpay) return true;
  return new Promise((resolve) => {
    const s = document.createElement("script");
    s.src = CHECKOUT_SCRIPT;
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

/**
 * Recurring Razorpay Subscription (mandate + auto-charge).
 * Uses subscription_id (not order_id) in Checkout — first charge authorizes the mandate,
 * subsequent renewals are auto-charged and reflected via `subscription.charged` webhook.
 */
export default function RazorpaySubscribeButton({ planSlug, billingCycle, label, highlight, testid }) {
  const { user } = useAuth();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const navigate = useNavigate();

  const subscribe = async () => {
    if (!user) { navigate("/signup"); return; }
    setErr("");
    setBusy(true);
    try {
      const scriptOk = await loadRazorpayScript();
      if (!scriptOk) {
        setErr("Could not load Razorpay Checkout. Please retry.");
        setBusy(false);
        return;
      }
      const { data } = await api.post("/razorpay/subscription/create", {
        plan_slug: planSlug,
        billing_cycle: billingCycle,
      });

      const rzp = new window.Razorpay({
        key: data.razorpay_key_id,
        subscription_id: data.subscription_id,
        name: "AllBusinessOnline",
        description: `${data.plan_name} · auto-renew ${billingCycle}`,
        prefill: { name: data.user.name || "", email: data.user.email || "" },
        theme: { color: "#38bdf8" },
        handler: async (response) => {
          try {
            await api.post("/razorpay/subscription/verify", {
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_subscription_id: response.razorpay_subscription_id,
              razorpay_signature: response.razorpay_signature,
            });
            navigate(`/payment/success?subscription_id=${response.razorpay_subscription_id}`);
          } catch (e) {
            const reason = formatApiErrorDetail(e.response?.data?.detail) || "verification-failed";
            navigate(`/payment/cancel?subscription_id=${response.razorpay_subscription_id}&reason=${encodeURIComponent(reason)}`);
          }
        },
        modal: {
          ondismiss: () => {
            setBusy(false);
            navigate(`/payment/cancel?subscription_id=${data.subscription_id}&reason=cancelled`);
          },
        },
      });
      rzp.on("payment.failed", (resp) => {
        const reason = resp?.error?.description || "payment-failed";
        navigate(`/payment/cancel?subscription_id=${data.subscription_id}&reason=${encodeURIComponent(reason)}`);
      });
      rzp.open();
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message || "Could not open subscription checkout.");
      setBusy(false);
    }
  };

  return (
    <div>
      <button
        data-testid={testid || `subscribe-${planSlug}-${billingCycle}`}
        onClick={subscribe}
        disabled={busy}
        className={`w-full py-3 rounded-md font-medium transition inline-flex items-center justify-center gap-2 ${
          highlight
            ? "bg-sky-400 hover:bg-sky-300 text-slate-950"
            : "border border-sky-400 text-sky-100 hover:bg-sky-400 hover:text-slate-950"
        } disabled:opacity-60`}
      >
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCcw className="w-4 h-4" />}
        {busy ? "Opening…" : (label || `Subscribe · auto-renew ${billingCycle}`)}
      </button>
      {err && <p className="text-xs text-red-400 mt-2 text-center">{err}</p>}
      <p className="text-[10px] font-mono text-slate-500 mt-2 text-center flex items-center justify-center gap-1">
        <ShieldCheck className="w-3 h-3" /> UPI AutoPay · Cards · eMandate · Cancel anytime
      </p>
    </div>
  );
}
